/**
 * The project at a glance: does it balance, does cash tie, and the year-by-year headline.
 */

import clsx from 'clsx';
import type { MComputed, MSource } from '../../../store/m-accounting';
import { periodLabel } from '../../../store/m-accounting';
import { formatMoney } from './MGrid';

/** Where a project's figures came from, as the overview says it. */
const SOURCE_KINDS: Record<string, string> = {
  pdf: 'accounts PDF',
  workbook: 'workbook',
  sample: 'sample filing',
  'companies-house': 'Companies House filing, read from its PDF',
  'companies-house-tags': "Companies House filing, read from its tagged figures (iXBRL)",
};


const LINES: { key: keyof MComputed['annualSummary']['years'][number]; label: string; percent?: boolean }[] = [
  { key: 'totalRevenue', label: 'Revenue' },
  { key: 'grossProfit', label: 'Gross profit' },
  { key: 'grossMargin', label: 'Gross margin', percent: true },
  { key: 'totalIndirectCosts', label: 'Indirect costs' },
  { key: 'ebitda', label: 'EBITDA' },
  { key: 'depreciation', label: 'Depreciation & amortisation' },
  { key: 'profitBeforeTax', label: 'Profit before tax' },
  { key: 'corporationTax', label: 'Tax' },
  { key: 'profitAfterTax', label: 'Profit after tax' },
  { key: 'netMargin', label: 'Net margin', percent: true },
];

export function MOverview({ computed, source }: { computed: MComputed; source?: MSource | null }) {
  const years = Object.keys(computed.annualSummary.years).map(Number).sort();
  // An error is a failure, not a nought.
  const failing = (v: number | string) => typeof v !== 'number' || Math.abs(v) >= 1;
  const show = (v: number | string) => (typeof v === 'number' ? formatMoney(v) : v);
  const unbalanced = Object.entries(computed.checks.balanceCheck).filter(([, v]) => failing(v));
  const cashChecked = Object.keys(computed.checks.cashVsAccounts).length > 0;
  const cashOut = Object.entries(computed.checks.cashVsAccounts).filter(([, v]) => failing(v));
  // A reading that does not balance still BALANCES here, because cash comes from the
  // cash flow: the gap moves into cash. Saying "balances" without that would be the
  // green light the IAS side rightly refuses to give for the same figures.
  const readingGap = Object.entries(source?.readingImbalance ?? {}).filter(([, v]) => Math.abs(v) >= 1);
  const bs = computed.tabs.find((t) => t.sheet === '(AI) Balance Sheet');
  const cashRow = bs?.rows.find((r) => r.row === 17);
  const equityRow = bs?.rows.find((r) => r.row === 38);

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2">
        <div
          className={clsx(
            'rounded-lg border px-4 py-3',
            unbalanced.length
              ? 'border-red-200 bg-red-50'
              : readingGap.length
                ? 'border-amber-200 bg-amber-50'
                : 'border-emerald-200 bg-emerald-50',
          )}
        >
          <div className="text-sm font-medium">
            {unbalanced.length
              ? 'Out of balance'
              : readingGap.length
                ? 'Balances - by carrying the reading’s gap in cash'
                : 'Balances in every year'}
          </div>
          <div className="mt-0.5 text-xs text-gray-600">
            {unbalanced.length
              ? unbalanced.map(([y, v]) => `${periodLabel(computed, y)}: ${show(v)}`).join(' · ')
              : readingGap.length
                ? `The reading of this filing does not balance (${readingGap
                    .map(([y, v]) => `${periodLabel(computed, y)}: ${show(v)}`)
                    .join(' · ')}). Cash here comes from the cash flow, so the sheet balances by holding that gap in cash - IAS accounting shows the same reading out of balance. Check the figures below against the filing.`
                : 'Cash comes from the cash flow - it is not a plug - and assets equal liabilities plus equity.'}
          </div>
        </div>
        {cashChecked ? (
          <div className={clsx('rounded-lg border px-4 py-3', cashOut.length ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50')}>
            <div className="text-sm font-medium">{cashOut.length ? 'Cash differs from the accounts' : 'Cash ties to the accounts'}</div>
            <div className="mt-0.5 text-xs text-gray-600">
              {cashOut.length
                ? cashOut.map(([y, v]) => `${periodLabel(computed, y)}: ${show(v)}`).join(' · ')
                : `In every year with the accounts' cash entered (${Object.keys(computed.checks.cashVsAccounts).join(', ')}) the cash flow arrives at it.`}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="text-sm font-medium">No filed cash to compare with</div>
            <div className="mt-0.5 text-xs text-gray-600">
              Enter the cash the accounts report on the Cashflow tab and the model's cash is checked against it.
            </div>
          </div>
        )}
      </div>

      {source?.filingChecks && source.filingChecks.length > 0 && <FilingChecks checks={source.filingChecks} />}

      {source && <ImportReport source={source} computed={computed} />}

      <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Summary</th>
              {years.map((y) => (
                <th key={y} className="px-3 py-2 text-right font-semibold">
                  {y}
                  <div className="text-[10px] font-normal uppercase text-gray-500">
                    {computed.historical.includes(computed.yearEnds?.[String(y)] ?? y) ? 'Historical' : 'Forecast'}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {LINES.map((line) => (
              <tr key={line.key} className="border-t border-gray-100">
                <td className="px-3 py-1.5">{line.label}</td>
                {years.map((y) => {
                  const value = computed.annualSummary.years[y][line.key];
                  return (
                    <td key={y} className="px-3 py-1.5 text-right font-mono text-xs">
                      {line.percent ? `${(value * 100).toFixed(1)}%` : formatMoney(value)}
                    </td>
                  );
                })}
              </tr>
            ))}
            {[{ row: cashRow, label: 'Cash at bank' }, { row: equityRow, label: 'Net assets' }].map(({ row, label }) => (
              <tr key={label} className="border-t border-gray-200 bg-gray-50/60">
                <td className="px-3 py-1.5 font-medium">{label}</td>
                {years.map((y) => {
                  // A year's balance is its last period's: Q4 in a quarterly model.
                  const end = computed.yearEnds?.[String(y)] ?? y;
                  const col = bs?.columns.find((c) => c.year === end)?.col;
                  return (
                    <td key={y} className="px-3 py-1.5 text-right font-mono text-xs">
                      {col && row ? formatMoney(row.cells[col]?.value ?? null) : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * What the import said, and the baseline model against IAS accounting on the same reading.
 *
 * The baseline model computes its own profit, depreciation and cash from its rows, so a figure
 * the import placed wrongly - or a treatment the two models do not share - shows here.
 */
/** The reading's own checks against the totals the filing prints - kept from the import. */
function FilingChecks({ checks }: { checks: NonNullable<MSource['filingChecks']> }) {
  const words = (key: string) => key.replace(/([A-Z])/g, ' $1').toLowerCase().replace(/^./, (c) => c.toUpperCase());
  return (
    <div className="overflow-hidden rounded-lg border border-amber-200 bg-white">
      <div className="border-b border-amber-200 bg-amber-50 px-4 py-3">
        <div className="text-sm font-medium text-amber-900">Where the reading disagrees with the filing</div>
        <div className="mt-0.5 text-xs text-amber-900">
          Each line below came out of the reading differently from the figure the filing prints. Both
          disciplines are built on this reading, so these are the figures to correct.
        </div>
      </div>
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 text-right text-[11px] uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-1.5 text-left font-semibold">Line</th>
            <th className="px-4 py-1.5 font-semibold">Year</th>
            <th className="px-4 py-1.5 font-semibold">Read</th>
            <th className="px-4 py-1.5 font-semibold">Filing prints</th>
            <th className="px-4 py-1.5 font-semibold">Difference</th>
          </tr>
        </thead>
        <tbody>
          {checks.map((c) => (
            <tr key={`${c.line}-${c.year}`} className="border-t border-gray-100">
              <td className="px-4 py-1.5 text-gray-900">{words(c.line)}</td>
              <td className="px-4 py-1.5 text-right text-gray-700">{c.year}</td>
              <td className="px-4 py-1.5 text-right font-mono text-gray-900">{formatMoney(c.read)}</td>
              <td className="px-4 py-1.5 text-right font-mono text-gray-900">{formatMoney(c.printed)}</td>
              <td className="px-4 py-1.5 text-right font-mono text-amber-800">{formatMoney(c.read - c.printed)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImportReport({ source, computed }: { source: MSource; computed: MComputed }) {
  const years = [...new Set(source.agreement.map((r) => r.year))].sort();
  const lines = [...new Set(source.agreement.map((r) => r.line))];
  const find = (line: string, year: number) =>
    source.agreement.find((r) => r.line === line && r.year === year);
  const differing = source.agreement.filter((r) => Math.abs(r.difference) >= 1);
  return (
    <div className="rounded-lg border border-gray-200">
      <div className="border-b border-gray-200 px-4 py-3">
        <div className="text-sm font-medium">
          Imported from {source.filename}
          <span className="ml-2 text-xs font-normal text-gray-500">
            {SOURCE_KINDS[source.kind] ?? source.kind} · {new Date(source.importedAt).toLocaleString()}
          </span>
        </div>
        <div className="mt-0.5 text-xs text-gray-600">
          {differing.length === 0
            ? "The baseline model agrees with the IAS accounting reading on every line below, in every filed year."
            : `${differing.length} of ${source.agreement.length} figures differ from the IAS accounting reading - highlighted below.`}
        </div>
        {source.notes.length > 0 && (
          <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-gray-700">
            {source.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        )}
      </div>
      <div className="overflow-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Baseline model vs IAS accounting</th>
              {years.map((y) => (
                <th key={y} className="px-3 py-2 text-right font-semibold">{periodLabel(computed, y)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line} className="border-t border-gray-100">
                <td className="px-3 py-1.5">{line}</td>
                {years.map((y) => {
                  const row = find(line, y);
                  const off = row !== undefined && Math.abs(row.difference) >= 1;
                  return (
                    <td
                      key={y}
                      className={clsx('px-3 py-1.5 text-right font-mono', off && 'bg-amber-50 text-amber-900')}
                      title={row ? `IAS accounting: ${formatMoney(row.accounting)}` : undefined}
                    >
                      {row ? formatMoney(row.mike) : ''}
                      {off && row && <div className="text-[10px]">vs {formatMoney(row.accounting)}</div>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {source.readingWarnings.length > 0 && (
        <details className="border-t border-gray-200 px-4 py-2 text-xs text-gray-700">
          <summary className="cursor-pointer text-gray-600">
            What the reader said about the filing ({source.readingWarnings.length})
          </summary>
          <ul className="mt-2 list-disc space-y-0.5 pl-5">
            {source.readingWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
